import Select, { SingleValue } from 'react-select';
import { ReactNode, useMemo } from 'react';
import type { OptionType } from '../../types/forms.type';

interface InputDropdownParams {
  id: string,
  label: string,
  selected: string,
  options: OptionType[],
  placeholder?: string,
  loadingMessage?: string,
  prefix?: string,
  placement: 'top' | 'auto' | 'bottom'
  onChange: (id: string, value: SingleValue<OptionType>) => void;
}
export default function Dropdown({
  id, label, selected, options, placeholder, loadingMessage, prefix, placement, onChange,
}: Readonly<InputDropdownParams>) {
  const selectedOption = useMemo(
    () => options.find((option: OptionType) => option.value === selected) ?? null,
    [options, selected],
  );
  const prefixElement = (children: ReactNode) => (
    <span className="inline-flex items-center">
      {prefix && <span className="mr-1 font-semibold text-base font-nunito leading-[21px]">{`${prefix}:`}</span>}
      <span className="text-body-large">{children}</span>
    </span>
  );

  return (
    <label htmlFor={id}>
      <div className="pb-3 text-label text-secondary ">{label}</div>
      <Select
        inputId={id}
        name={id}
        getOptionValue={(option) => option.value}
        menuPlacement={placement}
        placeholder={placeholder || 'Select...'}
        loadingMessage={() => loadingMessage || 'Loading...'}
        aria-label={label === '' ? (placeholder || 'Select...') : label}
        options={options}
        isSearchable
        isClearable
        isDisabled={options.length === 0}
        isLoading={options.length === 0}
        classNames={{
          valueContainer: () => 'flex justify-between items-center gap-1 pl-2 py-1.5',
          container: ({ isFocused }) => `flex! p-0! rounded-lg ${isFocused ? 'input-focused' : 'input-default'}`,
          control: () => 'w-full flex justify-between !flex !p-0',
          option: ({ isSelected }) => `text-body-large p-2 bg-transparent hover:text-secondary hover:bg-pipbg-subtle ${
            isSelected
              ? 'text-primary-dark font-bold '
              : 'text-secondary'
          }`,
          indicatorSeparator: () => 'hidden',
        }}
        styles={{ // keep basic default styles so not all custom
          control: () => ({}),
          option: () => ({}),
          valueContainer: () => ({}),
        }}
        value={selectedOption}
        onChange={(newValue) => onChange(id, newValue)}
        components={prefix ? {
          SingleValue: ({ children }) => prefixElement(children),
        } : undefined}
      />
    </label>
  );
}
